/**
 * Helpers for rendering unsafe-area ops alerts from API match payloads.
 */

const SEVERITY_RANK = {
  critical: 4,
  high: 3,
  medium: 2,
  low: 1,
};

/**
 * @param {{ matched?: boolean, highestSeverity?: string|null, hits?: Array }} matches
 * @returns {boolean}
 */
export function hasUnsafeAreaMatches(matches) {
  return Boolean(
    matches?.matched && Array.isArray(matches.hits) && matches.hits.length > 0
  );
}

/**
 * Tier 1–2 (critical/high) require booking acknowledgment.
 * @param {{ highestSeverity?: string|null }} matches
 * @returns {boolean}
 */
export function requiresUnsafeAreaAcknowledgment(matches) {
  if (!hasUnsafeAreaMatches(matches)) return false;
  const severity = matches.highestSeverity;
  return severity === "critical" || severity === "high";
}

/**
 * @param {string|null|undefined} severity
 * @returns {"error"|"warning"|"info"}
 */
export function unsafeAreaMuiSeverity(severity) {
  if (severity === "critical" || severity === "high") return "error";
  if (severity === "medium") return "warning";
  return "info";
}

/**
 * @param {string|null|undefined} severity
 * @returns {"error"|"warning"|"info"}
 */
export function unsafeAreaAntdType(severity) {
  if (severity === "critical" || severity === "high") return "error";
  if (severity === "medium") return "warning";
  return "info";
}

/**
 * @param {Object} hit
 * @returns {string}
 */
export function formatUnsafeAreaHit(hit) {
  const sideLabel = hit.side === "delivery" ? "Delivery" : "Pickup";
  const tierLabel = hit.tier
    ? `Tier ${hit.tier}`
    : hit.riskCategory || hit.severity || "";
  const concern = hit.primaryLogisticsConcern
    ? ` — Concern: ${hit.primaryLogisticsConcern}`
    : "";
  const protocol =
    hit.generalSafetyProtocol || hit.notes
      ? ` — Protocol: ${hit.generalSafetyProtocol || hit.notes}`
      : "";
  return `${sideLabel}: ${hit.name}${tierLabel ? ` (${tierLabel})` : ""}${concern}${protocol}`;
}

/**
 * @param {{ hits?: Array }} matches
 * @returns {string}
 */
export function formatUnsafeAreaDescription(matches) {
  if (!hasUnsafeAreaMatches(matches)) return "";
  return matches.hits.map(formatUnsafeAreaHit).join("\n");
}

/**
 * @param {{ highestSeverity?: string|null, highestTier?: number|null, hits?: Array }} matches
 * @returns {string}
 */
export function formatUnsafeAreaTitle(matches) {
  const count = matches?.hits?.length || 0;
  const tier = matches?.highestTier;
  const severity = matches?.highestSeverity;
  const tierPart = tier ? `Tier ${tier}` : null;
  const severityPart = severity || null;
  const label = [tierPart, severityPart].filter(Boolean).join(" · ");
  const suffix = label ? ` — ${label}` : "";
  if (count === 1) {
    return `Unsafe area reported${suffix}`;
  }
  return `Unsafe areas reported (${count})${suffix}`;
}

export { SEVERITY_RANK };
